/**
 * Sunayla custom elements. Registered once per page load.
 *
 * Loaded from snippets/scripts.liquid, which layout/theme.liquid renders at
 * L45 on every page and which sits outside every section-morph region. So the
 * definitions below are registered before any morph runs, and markup that
 * arrives later through a morph is upgraded by the browser automatically.
 *
 * Definitions must NOT sit inline in the snippet that renders their markup.
 * Per the HTML spec a script element inserted into a document through parsed
 * markup (innerHTML / morph) has its "already started" flag handling such that
 * it is never executed, so an element defined that way never upgrades and its
 * host markup falls back to whatever it plainly is - for sy-tote-upsell that
 * is a real <form action="/cart/add" method="post">, i.e. a full page POST.
 *
 * The tag in snippets/scripts.liquid must stay AFTER the importmap block: the
 * @shopify/events and @theme/* specifiers below resolve through that map.
 *
 * Measurements for this file (which engines, which cart start states, what
 * customElements.get returned) are in:
 *   plans/reports/sunayla-cro-260920-1252-safari-morph-root-fix.md
 */

import { CartLinesUpdateEvent } from '@shopify/events';
import { morphSection } from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';

/**
 * Upgrades the block's plain cart forms into the drawer's own ajax path.
 *
 * EVERY form inside the element, including forms that arrive LATER. The block
 * offers a matching tote AND matching socks (two forms). The submit listener
 * sits on the element itself and catches the bubbling event, it is NOT bound
 * per form. Binding per form in connectedCallback broke on 2026-10-06: adding
 * the tote morphs the drawer, the morph keeps this element (so connectedCallback
 * never runs again) but swaps in a fresh socks <form> with no listener, and the
 * second ADD did a plain POST to /cart. Do not go back to per-form binding.
 * The in-flight
 * flag stays on the element rather than per form on purpose: two adds racing
 * into the same cart would have the second one morph away the row the first is
 * still animating.
 *
 * Mirrors assets/product-form.js: ask /cart/add to re-render every
 * `cart-items-component` section, announce the change with a deferred
 * CartLinesUpdateEvent so the header bubble and the drawer react, then morph
 * the returned HTML in. Doing it any other way (bare fetch + reload, or a
 * hand-rolled DOM patch) desyncs the cart count in the header.
 */
if (!customElements.get('sy-tote-upsell')) {
  customElements.define(
    'sy-tote-upsell',
    class SyToteUpsell extends HTMLElement {
      #busy = false;

      connectedCallback() {
        this.addEventListener('submit', this.#onSubmit);
      }

      disconnectedCallback() {
        this.removeEventListener('submit', this.#onSubmit);
      }

      #onSubmit = async (event) => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement) || !this.contains(form)) return;

        event.preventDefault();
        if (this.#busy) return;
        this.#busy = true;

        const button = form.querySelector('button');
        const label = form.querySelector('[ref="label"]');
        let labelText = label?.textContent;
        if (button) button.disabled = true;
        if (label) label.textContent = 'Adding';

        // The drawer and the /cart page can both be mounted; re-render each.
        const sectionIds = [
          ...new Set(
            [...document.querySelectorAll('cart-items-component')]
              .map((node) => (node instanceof HTMLElement ? node.dataset.sectionId : null))
              .filter(Boolean)
          ),
        ];

        const body = new FormData(form);
        if (sectionIds.length) {
          body.append('sections', sectionIds.join(','));
          body.append('sections_url', window.location.pathname);
        }

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'cart',
            lines: [{ merchandiseId: String(body.get('id')), quantity: 1 }],
            promise: deferred.promise,
          })
        );

        try {
          const response = await fetch(
            Theme.routes.cart_add_url,
            fetchConfig('javascript', { body })
          );
          const parsed = await response.json();

          if (parsed.status) {
            throw new Error(parsed.description || parsed.message || 'Add to cart failed');
          }

          // Paint first. Morphing the drawer section re-renders this block from
          // the server, which is what makes it vanish now that the tote is in
          // the cart. It runs ahead of the event bookkeeping below so that a
          // failure there can never leave the shopper looking at a drawer that
          // disagrees with their cart.
          await Promise.all(
            sectionIds
              .filter((id) => parsed.sections?.[id])
              .map((id) => morphSection(id, parsed.sections[id], { mode: 'hydration' }))
          );

          // /cart/add returns the line that was added, NOT a cart object - it
          // carries no `currency`, and CartLinesUpdateEvent.createCartFromAjaxResponse
          // throws on it ("Currency code is required with currency style").
          // Horizon's own product-form.js re-reads the cart for exactly this
          // reason; do the same rather than hand the event the add response.
          const cartItems = document.querySelector('cart-items-component');
          let ajaxCart;
          if (cartItems) {
            await customElements.whenDefined('cart-items-component');
            ajaxCart = await cartItems.fetchCartData();
          } else {
            ajaxCart = await (await fetch(`${Theme.routes.cart_url}.js`)).json();
          }

          deferred.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
            detail: {
              sections: parsed.sections,
              items: ajaxCart.items,
              itemCount: ajaxCart.item_count,
              source: 'sy-tote-upsell',
              didError: false,
            },
          });
        } catch (error) {
          console.error('[sy-tote-upsell]', error);
          deferred.reject(error);

          // Deliberately NOT form.submit() here. A full-page POST would throw
          // the shopper out of an open drawer they were about to check out
          // from, and land them on Shopify's own error page if /cart/add is
          // the thing that is failing. This is an optional extra: the right
          // cost of a failed add is losing the add, never the cart. Say so on
          // the button and let them try again.
          this.dataset.failed = 'true';
          if (label) {
            label.textContent = 'Try again';
            labelText = null; // keep the retry wording; finally must not undo it
          }
        } finally {
          this.#busy = false;
          if (button) button.disabled = false;
          if (label && labelText) label.textContent = labelText;
        }
      };
    }
  );
}
